// Two-tier cache: an in-memory Map (per page session) backed by localStorage
// (survives reloads and navigation across the host site). Every storage access
// is guarded so disabled/full/private-mode storage silently degrades to
// memory-only rather than throwing into the host page.

const STORAGE_PREFIX = "tv:v1:";

export interface CacheEntry<T> {
  data: T;
  /** Epoch milliseconds the entry was written. */
  storedAt: number;
}

function storageKey(key: string): string {
  return `${STORAGE_PREFIX}${key}`;
}

/** Reads and validates a persisted entry; returns null on any failure. */
function readStorage<T>(key: string): CacheEntry<T> | null {
  try {
    const raw = window.localStorage.getItem(storageKey(key));
    if (!raw) return null;

    const parsed = JSON.parse(raw) as CacheEntry<T>;
    if (!parsed || typeof parsed.storedAt !== "number" || !("data" in parsed)) {
      return null; // Corrupt or partial — ignore.
    }
    return parsed;
  } catch {
    return null; // Storage disabled or malformed JSON.
  }
}

function writeStorage<T>(key: string, entry: CacheEntry<T>): void {
  try {
    window.localStorage.setItem(storageKey(key), JSON.stringify(entry));
  } catch {
    // Storage disabled/full/private mode — degrade to memory-only.
  }
}

export class VenueStore<T> {
  private memory = new Map<string, CacheEntry<T>>();

  /** Returns the cached entry (memory first, then storage) or null. */
  get(key: string): CacheEntry<T> | null {
    const cached = this.memory.get(key);
    if (cached) return cached;

    const stored = readStorage<T>(key);
    if (stored) this.memory.set(key, stored); // Promote into memory.
    return stored;
  }

  /** Writes an entry to both tiers, stamped with the current time. */
  set(key: string, data: T): CacheEntry<T> {
    const entry: CacheEntry<T> = { data, storedAt: Date.now() };
    this.memory.set(key, entry);
    writeStorage(key, entry);
    return entry;
  }

  delete(key: string): void {
    this.memory.delete(key);
    try {
      window.localStorage.removeItem(storageKey(key));
    } catch {
      // Nothing to clean up if storage is unavailable.
    }
  }
}
